// Bill Validation — shared TypeScript types
// All entities correspond to DB tables in migrations 022–024

// ============================================================================
// Shared
// ============================================================================

export type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
  /** A machine-readable reason, when the server sent one (e.g. 'ALREADY_SENDING'). */
  code?: string;
};

// ============================================================================
// Organisation (extends existing client table)
// ============================================================================

export type BvClientStatus = 'lead' | 'active' | 'onboarding';

export type BvClientExtn = {
  parent_org_id?: string;
  google_drive_link?: string;
  broker_id?: string;
  shield_platinum?: boolean;
};

export type BvOrganisation = {
  id: string;
  org_id: string;
  name: string;
  abn?: string;
  sf_account_id?: string;
  status: BvClientStatus;
  bill_val: 'active' | 'inactive';
  extn: { bv?: BvClientExtn } & Record<string, unknown>;
  created_at: string;
  // real columns (migration 052/055)
  broker?: string | null;
  shield_platinum?: boolean | null;
  drive_link?: string | null;
  site_count?: number;
  nmis?: string[] | null;
};

export type BvOrganisationFormData = {
  name: string;
  abn?: string;
  sf_account_id?: string;
  status: BvClientStatus;
  parent_org_id?: string;
  google_drive_link?: string;
  broker_id?: string;
  shield_platinum: boolean;
};

// ============================================================================
// Contact (extends existing contact table)
// ============================================================================

export type BvContactType = 'Billing' | 'Primary' | 'Technical' | 'Broker';

export type BvContactExtn = {
  mobile?: string;
  contact_type?: BvContactType;
  is_primary?: boolean;
};

export type BvContact = {
  id: string;
  org_id: string;
  client_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  role?: string;
  bv_extn: BvContactExtn;
  created_at: string;
  // real columns (migration 052)
  site_id?: string | null;
  mobile?: string | null;
  contact_type?: string | null;
  is_primary?: boolean | null;
};

export type BvContactFormData = {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  role?: string;
  mobile?: string;
  contact_type?: BvContactType;
  is_primary?: boolean;
};

// ============================================================================
// Site (extends existing site table)
// ============================================================================

export type BvSiteStatus =
  | 'Not Enrolled'
  | 'Pending Enrolment'
  | 'Enrolled'
  | 'Pending Cancellation'
  | 'Cancelled';

export type BvSiteExtn = {
  nmi_normalised?: string;
  state?: string;
  network_distributor?: string;
  network_tariff_code?: string;
  tni?: string;
  dlf_code?: string;
  /** A factor a person deliberately applied — typically the one the invoice
   *  itself states (see LossFactorOffer). Kept apart from the resolved value
   *  so an intentional choice can never be mistaken for a stale cache, and so
   *  it survives the table being reloaded for a new financial year. */
  mlf_override?: number | null;
  dlf_override?: number | null;
  mlf_code?: string;
  network_tariff_start?: string;
  /** When this site went on / came off its network DISTRIBUTOR. Distinct from
   *  `network_tariff_start`, which dates the tariff code — a site can change
   *  tariff without changing distributor. Informational: nothing in the shadow
   *  calculation selects by these. Blank end date = still with this distributor. */
  network_distributor_start_date?: string;
  network_distributor_end_date?: string;
  market_status?: string;
  market_region?: string;
  customer_classes?: string[];
  is_embedded?: boolean;
  bill_val_status?: BvSiteStatus;
  enrolment_date?: string | null;
  unenrolment_date?: string | null;
  /** Metering data provider — matches GCS folder name: 'mondo' | 'powermetric' */
  metering_provider?: 'mondo' | 'powermetric';
  /** Retail TOU schedule key — matches RETAIL_TOU_SCHEDULES, e.g. 'shell_qld' */
  retail_tou_schedule?: string;
  /**
   * Site network reliability measure — the level above which peak energy is
   * chargeable for this connection (usage-processing design §4.6). A SITE
   * attribute: same number whatever tariff the site is on, survives a tariff
   * change. Stored in the quoted unit and normalised to kW on read by
   * `reliabilityKw`. Absent means "skip the bucket" — never store `{ value: 0 }`
   * (the 068 CHECK constraint rejects it).
   */
  network_reliability?: BvNetworkReliability;
  /**
   * Network Specified Capacity — the capacity the distributor has agreed for
   * this connection (e.g. AusNet NSP75/76), charged per kVA per day whatever the
   * site draws. Read by the SITE_FIXED_VALUE treatment
   * (bv_treat_site_fixed_value, `source: specified_capacity`), which converts
   * MVA to kVA. Absent means the bucket prices at nothing.
   */
  specified_capacity?: BvSpecifiedCapacity;
  /**
   * AusNet Critical Peak Demand — the CPD kVA AusNet sets each year from the
   * site's demand on its nominated critical peak days, as printed on the
   * invoice. Read by the SITE_FIXED_VALUE treatment (`source: cpd_demand`,
   * migration 152) for the CRITICAL_PEAK_DEMAND bucket. Absent means the bucket
   * is left off the expected invoice.
   */
  cpd_demand?: BvSpecifiedCapacity;
};

/** Network Specified Capacity, as quoted by the distributor. */
export type BvSpecifiedCapacity = { value: number; uom: 'kVA' | 'MVA' };

/**
 * Network reliability measure for a site connection.
 * `value` is the quoted level; `uom` is the unit it was entered in.
 * Stored verbatim so the form can echo "2.5 MW" back to the user.
 * Normalise to kW at the call site via `reliabilityKw`.
 */
export type BvNetworkReliability = { value: number; uom: 'kW' | 'MW' };

/** Single conversion point. Absent → null, never 0. */
export const reliabilityKw = (r: BvNetworkReliability | null | undefined): number | null =>
  r == null ? null : r.uom === 'MW' ? r.value * 1000 : r.value;

export type BvEventType =
  | 'CRITICAL_PEAK' | 'CRITICAL_MINIMUM' | 'ENERGY_REWARD' | 'DEMAND_RESPONSE';

export type BvEvent = {
  id: string;
  org_id: string;
  distributor: string;
  event_type: BvEventType;
  event_name: string | null;
  event_date: string;      // YYYY-MM-DD
  time_start: string;      // HH:MM:SS
  time_end: string;        // HH:MM:SS, exclusive
  tariff_codes: string[] | null;
  market_region: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

/**
 * Per-rate-row overrides merged OVER bv_tariff_treatment.params at call time.
 * Open-ended by design — a new treatment adds keys here, not a migration — but
 * the known keys are typed so the form and the treatment functions agree.
 */
export type BvTreatmentParams = {
  /** Source interval the price assumes. Defaults to 30. — patterns A, B, C */
  interval_min?: number;
  /** Lookback for the 12-month capacity max. — pattern B */
  lookback_months?: number;
  /** Window a counter restarts on. — pattern C, "resetting monthly" */
  counter_reset?: 'daily' | 'monthly' | 'billing_period';
  /**
   * Annual free allowance, PRO-RATED BY DAYS BILLED before comparison.
   * — pattern D: "2,500 kWh per annum" → 205.5 kWh over a 30-day period.
   */
  free_allowance_kwh_pa?: number;
  /**
   * The period sum contributes nothing until it reaches this — event or not.
   * — pattern F: "dont charge until min threshold even if event happen".
   */
  min_threshold_kwh?: number;
  /** 'site_reliability' takes the level from site.bv_extn. — pattern G */
  threshold_source?: 'site_reliability';
  /**
   * Width of the chargeable band BELOW the site level; omit for "everything
   * above". — pattern G: "5 MW below the network reliability measure".
   */
  band_width_kw?: number;
} & Record<string, unknown>;

export type BvSiteNetwork = {
  id: string;
  org_id: string;
  tariff_code: string;
  tariff_name: string | null;
  distributor: string | null;
  description: string | null;
  name: string | null;
  channel: string | null;
  market_region: string | null;
  days_mapping_id: string | null;
  times_mapping_id: string | null;
  months_mapping_id: string | null;
  time_type_id: string | null;
  treatment_id: string | null;
  bucket_key: string | null;
  bill_category: string | null;
  demand_metric: BvDemandMetric | null;
  interval_min: number | null;
  sort_order: number;
  notes: string | null;
  treatment_params: BvTreatmentParams | null;
  event_type: BvEventType | null;
  category: string | null;
  open_to_new_applicants: boolean | null;
  price: number | null;
  price_type: string | null;
  created_at: string;
  updated_at: string;
};

export type BvTimesKind = 'window' | 'all' | 'residual' | 'none';
export type BvDemandMetric = 'kW' | 'kVA';
export type BvHolidayClass =
  | 'gazetted_full_day' | 'bank' | 'regional' | 'local' | 'part_day';

export type BvDaysMapping = {
  id: string;
  code: string;
  label: string;
  description: string | null;
  weekdays: number[];
  excluded_holiday_classes: BvHolidayClass[];
  notes: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type BvTimesMapping = {
  id: string;
  code: string;
  label: string;
  kind: BvTimesKind;
  /** Decimal hour, e.g. 15.5 = 15:30. Null unless kind === 'window'. */
  /** Ordered windows, end EXCLUSIVE; start >= end crosses midnight. A slot
   *  matches the code if it falls in ANY of them. Empty unless kind='window'. */
  windows: Array<{ start: number; end: number }>;
  /** GENERATED mirror of windows[0] since migration 113 — read-only. Kept for
   *  readers not yet moved to `windows`; writes must target `windows`. */
  start_hour: number | null;
  end_hour: number | null;
  description: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type BvMonthsMapping = {
  id: string;
  code: string;
  label: string;
  /** 1-12 */
  months: number[];
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type BvTimeType = {
  id: string;
  code: string;
  label: string;
  iana_tz: string | null;
  observes_dst: boolean;
  description: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type BvTariffTreatment = {
  id: string;
  code: string;
  label: string;
  function_name: string;
  params: Record<string, unknown>;
  applicable_duration: string | null;
  notes: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

/** One row from the bv_list_treatment_functions() RPC. */
export type TreatmentFunction = { function_name: string; arguments: string };

export type BvSiteNetworkFormData = {
  tariff_code: string;
  tariff_name: string;
  distributor: string;
  description: string;
  name: string;
  channel: string;
  market_region: string;
  days_mapping_id: string;
  times_mapping_id: string;
  months_mapping_id: string;
  time_type_id: string;
  treatment_id: string;
  bucket_key: string;
  bill_category: string;
  demand_metric: string;
  interval_min: string;
  notes: string;
  /** Raw JSON text from the form's textarea; parsed and validated on save. */
  treatment_params: string;
  event_type: string;
  category: string;
  open_to_new_applicants: boolean;
  price: string;
  price_type: string;
};

/** Simple usage value per tariff code: { "EA010": 12500.5 } */
export type BvTariffUsage = number;

/**
 * When a site was on one network tariff code (migration 099).
 *
 * `to: null` means still on it — the same convention the distributor end date
 * uses. Keyed by code, not by bucket row: a site is on a CODE for a period, and
 * every bucket under that code shares the window.
 */
export type BvTariffPeriod = {
  from?: string | null;
  to?: string | null;
};

/** The `bv_dma` rows joined onto a site query, NOT the full table row — the
 *  select is `bv_dma(id, dma_provider, start_date, end_date)`, so org_id,
 *  site_id and the timestamps are absent and the nullable columns arrive as
 *  `null`, not `undefined`. The full row is `BvDma` further down; this was
 *  declared under that name too, which the server build rejected as a duplicate
 *  identifier. */
export type BvSiteDma = {
  id: string;
  dma_provider?: string | null;
  start_date?: string | null;
  end_date?: string | null;
};

export type BvMeteringContact = {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  role?: string | null;
};

export type BvSite = {
  id: string;
  org_id: string;
  client_id: string;
  name?: string;
  nmi?: string;
  addr_street?: string;
  addr_city?: string;
  addr_state?: string;
  addr_postcode?: string;
  market_region?: string;
  current_retailer?: string | null;
  current_contract_expiry?: string | null;
  retailer_contract_start?: string | null;
  previous_retailer?: string | null;
  dma_status?: string;
  loa_expiry_date?: string | null;
  shield_platinum?: boolean | null;
  meter_install_date?: string | null;
  bv_extn: BvSiteExtn;
  network_codes: string[];
  network_tariff_usage: Record<string, BvTariffUsage>;
  /** When each assigned code applied to this site (migration 099). Absent on a
   *  site whose windows have never been recorded. */
  network_tariff_dates?: Record<string, BvTariffPeriod>;
  sf_site_id?: string;
  drive_link?: string;
  created_at: string;
  // freshness (migration 065) — updated_at is trigger-maintained on any edit,
  // sf_synced_at only moves when the Salesforce refresh runs.
  updated_at?: string | null;
  sf_synced_at?: string | null;
  // real columns (migration 052)
  is_embedded?: boolean | null;
  market_status?: string | null;
  // joined
  client?: Pick<BvOrganisation, 'id' | 'name' | 'sf_account_id'>;
  bv_dma?: BvSiteDma[] | null;
  metering_contact?: BvMeteringContact | null;
  // derived (populated by siteService.list)
  active_retailer?: string;
};

export type BvSiteFormData = {
  nmi: string;
  client_id: string;
  sf_site_id?: string;
  addr_street?: string;
  addr_city?: string;
  addr_state?: string;
  addr_postcode?: string;
  state?: string;
  network_distributor?: string;
  network_tariff_code?: string;
  tni?: string;
  dlf_code?: string;
  mlf_code?: string;
  /** Deliberate override of the resolved factor — see BvSiteExtn. '' clears it
   *  and hands the site back to the loss factor table. */
  mlf_override?: number | '' | null;
  dlf_override?: number | '' | null;
  network_tariff_start?: string;
  /** Distributor validity window — see the note on BvSiteBvExtn. Empty string
   *  clears the stored value, matching how the other date fields on this form
   *  behave. */
  network_distributor_start_date?: string;
  network_distributor_end_date?: string;
  market_status?: string;
  market_region?: string;
  customer_classes?: string;
  is_embedded?: boolean;
  bill_val_status?: BvSiteStatus;
  enrolment_date?: string | null;
  unenrolment_date?: string | null;
  metering_provider?: 'mondo' | 'powermetric' | '';
  retail_tou_schedule?: string;
  /**
   * Network reliability measure — flat form-state mirror of
   * `bv_extn.network_reliability`. Empty string means "no value" and MUST
   * save as ABSENT (the `network_reliability` key omitted from bv_extn),
   * never as `{ value: 0 }` — a zero threshold would bill the site's entire
   * consumption at the peak rate, and the 068 CHECK constraint rejects it.
   */
  network_reliability_value?: number | '';
  network_reliability_uom?: 'kW' | 'MW' | '';
  /** `bv_extn.specified_capacity`, flat. Empty string saves as absent. */
  specified_capacity_value?: number | '';
  specified_capacity_uom?: 'kVA' | 'MVA' | '';
  /** `bv_extn.cpd_demand`, flat. Empty string saves as absent. */
  cpd_demand_value?: number | '';
  cpd_demand_uom?: 'kVA' | 'MVA' | '';
};

// ============================================================================
// NMI Allocation (reference table)
// ============================================================================

export type BvNmiAllocation = {
  id: string;
  state: string;
  distributor: string;
  nmi_from: string;
  nmi_to: string;
};

// ============================================================================
// Unified contract (bv_contract table — replaces 5 old tables)
// ============================================================================

// 'distribution' is the retired Distribution Config shape (job 35). Rows still
// exist in the database but nothing reads or writes them — network charges are
// 'network' contracts now, dated per tariff code.
export type BvContractType = 'retail' | 'distribution' | 'environmental' | 'network';

export type BvContract = {
  id: string;
  org_id: string;
  site_id: string;
  contract_type: BvContractType;
  valid_from?: string;
  valid_to?: string;
  is_current: boolean;
  data: Record<string, unknown>;
  notes?: string;
  created_at: string;
  updated_at: string;
};

// ============================================================================
// Retail Contract
// ============================================================================

export type BvRateType = 'smooth' | 'stepped';
export type BvRateChargeUnit =
  | 'per_meter_day' | 'per_meter_month' | 'per_meter_year'
  | 'per_servicepoint_day' | 'per_servicepoint_month' | 'per_servicepoint_year';

export type BvRetailContract = {
  id: string;
  org_id: string;
  site_id: string;
  retailer_id?: string;
  contract_start?: string;
  contract_end?: string;
  rate_type: BvRateType;
  source?: string;
  is_current: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
  // joined
  retailer?: { id: string; name: string };
  rates?: BvRetailContractRate[];
};

export type BvRetailContractFormData = {
  retailer_id?: string;
  retailer_name?: string;
  contract_start?: string;
  contract_end?: string;
  rate_type: BvRateType;
  source?: string;
  is_current?: boolean;
  notes?: string;
};

export type BvRetailContractRate = {
  id: string;
  period_start?: string;
  period_end?: string;
  // Usage rates (c/kWh)
  peak_rate?: number;
  shoulder_rate?: number;
  off_peak_rate?: number;
  anytime_rate?: number;
  super_peak_rate?: number;
  // Supply
  supply_charge?: number;
  supply_unit?: BvRateChargeUnit;
  // Feed-in tariffs (c/kWh)
  feed_in_rate?: number;
  feed_in_rate_peak?: number;
  feed_in_rate_off_peak?: number;
  feed_in_rate_shoulder?: number;
  feed_in_rate_anytime?: number;
  // Metering & services
  metering_rate?: number;
  metering_unit?: BvRateChargeUnit;
  supplementary_metering_rate?: number;
  supplementary_metering_unit?: BvRateChargeUnit;
  supplementary_services_rate?: number;
  supplementary_services_unit?: BvRateChargeUnit;
  green_power_rate?: number;
};

export type BvRetailContractRateFormData = {
  period_start?: string;
  period_end?: string;
  // Usage rates (c/kWh)
  peak_rate?: number;
  shoulder_rate?: number;
  off_peak_rate?: number;
  anytime_rate?: number;
  super_peak_rate?: number;
  // Supply
  supply_charge?: number;
  supply_unit?: BvRateChargeUnit;
  // Feed-in tariffs (c/kWh)
  feed_in_rate?: number;
  feed_in_rate_peak?: number;
  feed_in_rate_off_peak?: number;
  feed_in_rate_shoulder?: number;
  feed_in_rate_anytime?: number;
  // Metering & services
  metering_rate?: number;
  metering_unit?: BvRateChargeUnit;
  supplementary_metering_rate?: number;
  supplementary_metering_unit?: BvRateChargeUnit;
  supplementary_services_rate?: number;
  supplementary_services_unit?: BvRateChargeUnit;
  green_power_rate?: number;
};

// ============================================================================
// DMA (bv_dma table — provider + validity window only; migration 036)
// ============================================================================

export type BvDma = {
  id: string;
  org_id: string;
  site_id: string;
  dma_provider?: string;
  start_date?: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
};

export type BvDmaFormData = {
  dma_provider?: string;
  start_date?: string;
  end_date?: string;
};

// ============================================================================
// Network Tariff Contract (job 35)
//
// Replaces Distribution Config. A site's use of one network tariff code, dated,
// with its charges in rate periods — the same shape as a retail contract, so the
// two cards on the site page read alike and share bv_contract/bv_contract_rate.
//
// `tariff_code` is stored in bv_contract.supplier_key (not only in `data`), so
// the existing uniq_contract_deal index blocks a duplicate code+window per site.
// ============================================================================

export type BvNetworkContract = {
  id: string;
  org_id: string;
  site_id: string;
  tariff_code: string;
  tariff_name?: string;
  distributor?: string;
  /** When THIS SITE moved onto the code. Not the distributor's price window. */
  valid_from?: string;
  /** Absent while the site is still on the code. */
  valid_to?: string;
  rate_type: BvRateType;
  /** A demand contract is a fact of its own — Jemena sites carry it with no kVA. */
  has_demand_contract: boolean;
  open_to_new?: boolean;
  is_current: boolean;
  source?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  rates?: BvNetworkRate[];
};

export type BvNetworkContractFormData = {
  tariff_code?: string;
  tariff_name?: string;
  distributor?: string;
  valid_from?: string;
  valid_to?: string;
  rate_type: BvRateType;
  has_demand_contract?: boolean;
  is_current?: boolean;
  notes?: string;
};

export type BvNetworkRate = {
  id: string;
  period_start?: string;
  period_end?: string;
  // Network usage (c/kWh)
  peak_rate?: number;
  shoulder_rate?: number;
  off_peak_rate?: number;
  anytime_rate?: number;
  // Demand ($/kVA/year) and the contracted quantities it is charged on
  peak_demand_rate?: number;
  anytime_demand_rate?: number;
  capacity_kw?: number;
  demand_kva?: number;
  // Network standing charge
  supply_charge?: number;
  supply_unit?: BvRateChargeUnit;
};

export type BvNetworkRateFormData = Omit<BvNetworkRate, 'id'>;

// ============================================================================
// Environmental Config
// ============================================================================

export type BvRateMode = 'fixed' | 'variable';

// Certificate types available per state
export const ENV_CERT_TYPES_BY_STATE: Record<string, string[]> = {
  VIC: ['VEEC', 'LREC', 'SREC'],
  NSW: ['ESC', 'PDRS', 'LREC', 'SREC'],
  SA: ['REPS', 'LREC', 'SREC'],
  ACT: ['AEEC', 'LREC', 'SREC'],
  QLD: ['LREC', 'SREC'],
  TAS: ['LREC', 'SREC'],
  ALL: ['LREC', 'SREC'],
};

export type BvEnvironmentalConfig = {
  id: string;
  org_id: string;
  site_id: string;
  certificate_type: string;
  rate_mode: BvRateMode;
  period_start?: string;
  period_end?: string;
  rate?: number;
  rate_unit?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
};

export type BvEnvironmentalConfigFormData = {
  certificate_type: string;
  rate_mode?: BvRateMode;
  period_start?: string;
  period_end?: string;
  rate?: number;
  rate_unit?: string;
  notes?: string;
};

// ============================================================================
// Attachment
// ============================================================================

export type BvAttachmentCategory =
  | 'loa'
  | 'electricity_contract'
  | 'dma_contract'
  | 'invoice'
  | 'meter_data'
  | 'nta_report'
  | 'other';

export const ATTACHMENT_CATEGORY_LABELS: Record<BvAttachmentCategory, string> = {
  loa: 'LOA',
  electricity_contract: 'Electricity Contract',
  dma_contract: 'DMA Contract',
  invoice: 'Invoice',
  meter_data: 'Meter Data',
  nta_report: 'NTA Report',
  other: 'Other',
};

export type BvAttachment = {
  id: string;
  org_id: string;
  site_id?: string;
  file_name: string;
  file_type?: string;
  file_size_bytes?: number;
  category: BvAttachmentCategory;
  storage_path: string;
  loa_signed_date?: string;
  loa_expiry_date?: string;
  uploaded_by?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  // transient — signed URL from Supabase Storage
  url?: string;
};

// ============================================================================
// Note Template
// ============================================================================

export type BvNoteTemplateCategory = 'Overcharge' | 'Undercharge' | 'GST' | 'Tariff' | 'Default';

export type BvNoteTemplate = {
  id: string;
  name: string;
  category: BvNoteTemplateCategory;
  customer_text: string;
  is_active: boolean;
  created_at: string;
};

// ============================================================================
// NEM Interval (bv_nem_interval) — one row per 5-min slot
// Raw storage, retained 50 days then auto-deleted at sync time.
// Aggregated into bv_daily_usage after each sync.
// ============================================================================

export type BvNemInterval = {
  id:            string;
  org_id:        string;
  site_id:       string;
  nmi:           string;
  nmi_suffix:    string;
  interval_date: string;       // YYYY-MM-DD
  slot_number:   number;       // 0-based: 0=00:00, 1=00:05, ..., 287=23:55 (5-min)
  kwh:           number;
  quality_flag:  string;       // 'A' | 'E' | 'S'
  interval_min:  number;       // 5 | 15 | 30
  provider:      string;
  source_file:   string | null;
  sync_log_id:   string | null;
  created_at:    string;
};

// ============================================================================
// Daily Usage (bv_daily_usage) — one row per site per calendar day
// Aggregated from bv_nem_interval after each sync.
// total_kwh = raw sum. TOU split at bill time via auto-populate-usage.
// ============================================================================

export type BvDailyUsage = {
  id:             string;
  org_id:         string;
  site_id:        string;
  nmi:            string;
  usage_date:     string;        // YYYY-MM-DD
  total_kwh:      number;        // raw sum, no TOU schedule applied
  peak_kwh:       number;        // 0 at sync time
  shoulder_kwh:   number;        // 0 at sync time
  offpeak_kwh:    number;        // 0 at sync time
  interval_min:   number;        // 5 | 15 | 30
  source:         'gcs' | 'manual';
  sync_log_id:    string | null;
  created_at:     string;
  updated_at:     string;
};

// ============================================================================
// GCS Sync Log (extended after migration 040)
// ============================================================================

export type BvGcsSyncLog = {
  id:                     string;
  gcs_path:               string;
  provider:               string;
  file_size_bytes:        number | null;
  processed_at:           string;
  status:                 'success' | 'error' | 'skipped';
  error_message:          string | null;
  nmis_found:             string[];
  nmis_matched:           string[];
  nmis_unmatched:         string[];
  interval_rows_inserted: number;
  nmi:                    string | null;
  date_from:              string | null;  // YYYY-MM-DD
  date_to:                string | null;  // YYYY-MM-DD
  interval_min:           number;
  row_count:              number | null;
};

// ============================================================================
// Audit trail (migration 066)
// ============================================================================

export type BvAuditEntityType =
  | 'site' | 'bill' | 'contract' | 'organisation' | 'invoice' | 'contact' | 'network_tariff';

export type BvAuditAction =
  | 'created' | 'updated' | 'deleted'
  | 'note'
  | 'validation'
  | 'manual_override'
  | 'recalculation'
  | 'assigned'
  | 'status_change'
  | 'archived'          // job 81
  | 'restored';         // job 81

export type BvAuditEvent = {
  id:          string;
  org_id:      string;
  entity_type: BvAuditEntityType;
  entity_id:   string;
  site_id:     string | null;
  action:      BvAuditAction;
  field:       string | null;
  old_value:   unknown;
  new_value:   unknown;
  note:        string | null;
  actor_id:    string | null;
  actor_email: string | null;
  assigned_to: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at:  string;
  // joined
  assignee?:   { id: string; email: string | null; full_name?: string | null } | null;
};

/** What a caller hands to auditService.log — org, actor and timestamp are filled in. */
export type BvAuditEventInput = {
  entity_type: BvAuditEntityType;
  entity_id:   string;
  site_id?:    string | null;
  action:      BvAuditAction;
  field?:      string | null;
  old_value?:  unknown;
  new_value?:  unknown;
  note?:       string | null;
  assigned_to?: string | null;
};

// ============================================================================
// Usage processing (migrations 069-074) — pass-1 stored rows + bucket rollup
// ============================================================================

/** 'day_count' is the daily-charge method added in migration 078 — a $/day
 *  bucket's quantity is a count of qualifying days, not an interval sum. */
export type BvAggMethod = 'sum' | 'max' | 'top_n_avg' | 'accumulate' | 'day_count';
export type BvSourceFidelity = 'raw' | 'daily';
export type BvTouBand = 'superpeak' | 'peak' | 'shoulder' | 'offpeak';

export type BvDistributionTariffUsage = {
  id: string;
  site_id: string;
  nmi: string;
  nmi_suffix: string;
  usage_date: string;
  bucket_key: string;
  bucket_label: string | null;
  channel: string;
  tariff_code: string;
  distributor: string;
  quantity: number;
  uom: string;
  agg_method: BvAggMethod;
  agg_n: number | null;
  rate: number | null;
  price_type: string | null;
  cost: number | null;
  event_id: string | null;
  config_hash: string;
  slot_count: number | null;
  source_fidelity: BvSourceFidelity;
  is_stale: boolean;
  stale_reason: string | null;
  computed_at: string;
  run_id: string | null;
};

export type BvDistributionTreatmentInterval = {
  id: string;
  site_id: string;
  nmi_suffix: string;
  usage_date: string;
  /** HH:MM:SS, always on a 30-minute boundary. */
  interval_start: string;
  bucket_key: string;
  kwh: number;
  power_gross: number;
  threshold_kw: number | null;
  power_chargeable: number;
  uom: string;
  event_id: string | null;
  src_slot_count: number;
  quality_mix: string | null;
  is_stale: boolean;
  computed_at: string;
};

export type BvRetailTouUsage = {
  id: string;
  site_id: string;
  nmi: string;
  nmi_suffix: string;
  usage_date: string;
  band: BvTouBand;
  quantity: number;
  retailer_name: string;
  state: string;
  rate: number | null;
  cost: number | null;
  holiday_name: string | null;
  is_stale: boolean;
  computed_at: string;
};

/** One bucket, reduced over a period. `basis` says how. */
export type BucketRollup = {
  bucket_key: string;
  quantity: number;
  uom: string;
  /**
   * The reducer function name returned by bv_reduce_bucket, e.g.
   * 'bv_treat_max_interval_billing_month'. A string (not a fixed union)
   * because the Task 9 dispatch redesign made bv_reduce_bucket return the
   * executed reducer's function_name, not the legacy agg-method enum.
   */
  basis: string;
  rows_considered: number;
  stale: boolean;
  /** Migration 080 additions — the treatment behind `basis`, in human terms,
   *  plus the bucket's price so a reduced figure can be costed in place.
   *  `cost` is null when price_type is not convertible to dollars. */
  treatment_code: string | null;
  treatment_label: string | null;
  applicable_duration: string | null;
  rate: number | null;
  price_type: string | null;
  cost: number | null;
};

/**
 * One configured bucket on the site's network tariff, from
 * bv_site_tariff_config (migration 081). This is the CONFIGURED set — the
 * stepper joins results onto it so a bucket that produced no rows is still
 * shown, with a reason, rather than silently missing.
 */
export type BvTariffBucketConfig = {
  bucket_key: string;
  bucket_label: string;
  category: string | null;
  channel: string;
  tariff_name: string | null;
  tariff_code: string;
  distributor: string;
  price: number | null;
  price_type: string | null;
  demand_metric: string | null;
  treatment_code: string | null;
  treatment_label: string | null;
  treatment_kind: string | null;
  applicable_duration: string | null;
  sort_order: number;
  notes: string | null;
  /** days_mapping_id AND times_mapping_id both set — the RPC cannot classify without them. */
  has_vocabulary: boolean;
  /** $/day or c/day — priced per day, never interval-classified. */
  is_daily: boolean;
  times_kind: string | null;
  /** Human-readable classification window, e.g. '3pm-9pm, June-August and November-March'. */
  window_label: string;
  /** false when price_type cannot be converted to dollars (e.g. 'c/kWh/day' on a kVA quantity). */
  cost_convertible: boolean;
};
